import { Button, IconButton } from '@creditornot/cb-button';
import { ShareAndroid } from '@creditornot/cb-icons';
import { typographyCss } from '@creditornot/cb-ingredients';
import { LegacyPopover } from '@creditornot/cb-popover';
import React, { forwardRef, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import styled from 'styled-components';

import { useListingManagerRoutes } from 'modules/shared-menu/hooks/useListingManagerRoutes';
import { SharedMenuActions } from 'modules/shared-menu/pages/SharedMenuEditorPage/components/SharedMenuActions';
import { SharedMenu } from 'modules/shared-menu/types';
import { isNewSelection, isSharedMenuBootstrapping } from 'modules/shared-menu/utils';

type SelectionActionsProps = {
  onEditSelectionVenues: (selection: SharedMenu) => void;
  selection: SharedMenu;
  venueCount: number;
};

const ActionsContainer = styled.div`
  display: flex;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ShareButtonTooltip = styled.span`
  ${typographyCss.Body3()}
  display: flex;
  width: 300px;
`;

export const SelectionActions = forwardRef<HTMLDivElement, SelectionActionsProps>(
  ({ onEditSelectionVenues, selection, venueCount }, ref) => {
    const history = useHistory();
    const { menuEditorPage } = useListingManagerRoutes();

    const selectionCanBeShared = useMemo(() => {
      if (!selection) {
        return false;
      }
      return isNewSelection(selection) === false;
    }, [selection]);

    const canEditVenues = selectionCanBeShared || venueCount > 0;

    const bootstrappingInProgress = useMemo(() => isSharedMenuBootstrapping(selection.draft), [selection.draft]);

    return (
      <ActionsContainer>
        <ButtonGroup ref={ref}>
          <Button
            variant="lightBlue"
            size="tiny"
            onClick={() => {
              history.push(menuEditorPage());
            }}
          >
            <FormattedMessage id="listing-manager.selections.actions.edit-selection" />
          </Button>
          <LegacyPopover
            showOnHover
            disabled={canEditVenues}
            content={
              <ShareButtonTooltip>
                <FormattedMessage id="shared-menu.link.unpublished-selection-tooltip" />
              </ShareButtonTooltip>
            }
          >
            <Button
              disabled={bootstrappingInProgress || !canEditVenues}
              variant="lightBlue"
              size="tiny"
              onClick={() => onEditSelectionVenues(selection)}
              icon={<ShareAndroid />}
            >
              <FormattedMessage id="listing-manager.selections.actions.edit-venues" />
            </Button>
          </LegacyPopover>
          <IconButton variant="blue" size="tiny">
            <SharedMenuActions sharedMenu={selection} />
          </IconButton>
        </ButtonGroup>
      </ActionsContainer>
    );
  },
);
